<template>
  <div id="patient-registry" class="registry">
    <h2 class="registry__header" v-html="titleText[formFunction]" />

    <FormTemplate
      class="registry__form"
      :schema="schema"
      :model="model"
      ref="formTemplate"
      @submit.prevent="submitMdm"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import { removeAccents } from '@/utils/helpers';
import { dataLayersMixin } from '@/utils/mixins';
import cepServiceMixin from '@/utils/mixins/services/cepService.mixin';
import FormTemplate from '@/components/commons/forms/FormTemplate.vue';
import debounce from 'lodash/debounce';
import { addDynatraceIdentifier } from '@/utils/helpers/addDynatraceIdentifier';

import { LOCAL_STORAGE_UNIT_ID } from '@/utils/constants/checkin.constants';

import {
  PATIENT_WITHOUT_MDMID,
  PATIENT_WITHOUT_ADMISSION,
  ERROR_CREATE_ADMISSION_VACCINE,
} from '@/utils/constants/datalayerMessage.constants';
import datalayerErrorMessage from '@/utils/helpers/message/datalayerErrorMessage.js';

export default {
  name: 'PatientRegistry',

  components: { FormTemplate },

  mixins: [cepServiceMixin, dataLayersMixin],

  data() {
    return {
      cepIsValid: true,
      mdmCep: true,
      titleText: {
        updateInfo: 'Confirme seus dados pessoais e ajuste os dados que estiverem desatualizados.',
        createInfo: 'Preencha os campos abaixo com seus dados para continuar seu atendimento.',
      },
      model: {
        name: '',
        birthDate: '',
        biologicalSex: '',
        email: '',
        phone: '',
        cep: '',
        street: '',
        streetNumber: '',
        addressComplement: '',
        neighborhood: '',
        state: '',
        city: '',
      },
      schema: {
        fields: [
          {
            type: 'input',
            id: 'registry-input-name',
            label: '* Nome Completo',
            model: 'name',
            rules: 'required|fullname',
            readonly: false,
            ShowFieldForInCompany: true,
          },
          {
            type: 'input',
            id: 'registry-input-birthDate',
            label: '* Data de nascimento',
            model: 'birthDate',
            rules: 'required|date',
            readonly: false,
            ShowFieldForInCompany: true,
          },
          {
            type: 'select',
            id: 'registry-select-biologicalSex',
            label: '* Sexo Biológico',
            model: 'biologicalSex',
            options: [
              { label: 'Feminino', value: 'FEMININO' },
              { label: 'Masculino', value: 'MASCULINO' },
            ],
            rules: 'required',
            readonly: false,
            ShowFieldForInCompany: true,
          },
          {
            type: 'input',
            id: 'registry-input-email',
            label: '* E-mail',
            model: 'email',
            rules: 'required|email',
            readonly: false,
            ShowFieldForInCompany: true,
          },
          {
            type: 'input',
            id: 'registry-input-tel',
            label: '* Celular',
            model: 'phone',
            rules: 'required|phone',
            readonly: false,
            ShowFieldForInCompany: true,
          },
          {
            type: 'input',
            id: 'registry-input-address-cep',
            label: '* CEP',
            model: 'cep',
            rules: 'required|cep',
            readonly: false,
          },
          {
            type: 'link',
            id: 'unkown-cep-link',
            label: 'Não sei meu CEP',
            linkRef: 'https://buscacepinter.correios.com.br/app/endereco/index.php',
          },
          {
            type: 'input',
            id: 'registry-input-address-state',
            label: '* Estado',
            model: 'state',
            rules: 'required|min:3',
            readonly: false,
            size: 'xs',
          },
          {
            type: 'input',
            id: 'registry-input-address-city',
            label: '* Cidade',
            model: 'city',
            rules: 'required',
            readonly: false,
            size: 'xs',
          },
          {
            type: 'input',
            id: 'registry-input-address-street',
            label: '* Rua/Logradouro',
            model: 'street',
            rules: 'required',
            readonly: false,
          },
          {
            type: 'input',
            id: 'registry-input-address-number',
            label: '* Número',
            model: 'streetNumber',
            rules: 'required',
            readonly: false,
          },
          {
            type: 'input',
            id: 'registry-input-address-complement',
            label: 'Complemento',
            model: 'addressComplement',
            rules: 'alpha_num_spaces',
            readonly: false,
          },
          {
            type: 'input',
            id: 'registry-input-address-neighborhood',
            label: '* Bairro',
            model: 'neighborhood',
            rules: 'required|alpha_num_spaces',
            readonly: false,
          },
        ],
      },
    };
  },

  computed: {
    ...mapState({
      chosenContactType: ({ validation }) => validation.data.chosenContactType,
      contactSelected: ({ validation }) => validation.data.contactSelected,
      cpfSearched: ({ cpfSearched }) => cpfSearched.data.cpf,
      currentAdmission: ({ currentAdmission }) => currentAdmission.data,
      medicalOrders: ({ currentAdmission }) => currentAdmission.data.medicalOrders,
      isAdmissionAuthorized: ({ global }) => global.isAdmissionAuthorized,
      mdmData: ({ mdm }) => mdm.data,
      patientData: ({ registrationData }) => registrationData,
      admission: ({ currentAdmission }) => currentAdmission.data,
    }),

    ...mapGetters({
      getBrazilStateFullname: 'global/getBrazilStateFullname',
      isT2FlowAllowed: 'global/isT2FlowAllowed',
      isVaccineFlow: 'global/isVaccineFlow',
      isCollaboratorFlow: 'global/isCollaboratorFlow',
      hasExams: 'haveScheduledExams',
      getBrandUnitIdVariables: 'getBrandUnitIdVariables',
      enumUnitSelected: 'enumUnitSelected',
      admissionFlows: 'global/admissionFlows',
      cpfHidden: 'cpfHidden',
    }),

    // Gets what type of use the form will have, create/update
    formFunction() {
      return this.mdmData.id ? 'updateInfo' : 'createInfo';
    },

    // return an Array with the missing address fields
    emptyAddressFields() {
      return Object.keys(this.model).filter(
        (field) =>
          (field === 'state' ||
            field === 'city' ||
            field === 'neighborhood' ||
            field === 'street') &&
          this.model[field] === '',
      );
    },
  },

  watch: {
    'model.cep': {
      async handler(newVal, oldVal) {
        if (newVal !== oldVal && newVal.length === 9) {
          if (this.mdmCep && !this.emptyAddressFields.length) {
            this.mdmCep = false;
            return;
          }

          const addressData = await this.searchAddressByCep(newVal);

          if (!this.cepIsValid) {
            this.mdmCep = false;
            this.updateAddressFields(addressData);
            return;
          }

          if (!this.mdmCep) {
            this.updateAddressFields(addressData);
            this.registrationData(this.model);
            return;
          }

          Object.assign(this.model, addressData);
        }
      },
    },

    'model.name': {
      handler(newVal) {
        this.debounceFieldChange('name')(newVal);
      },
    },
    'model.birthDate': {
      handler(newVal) {
        this.debounceFieldChange('birthDate')(newVal);
      },
    },
    'model.biologicalSex': {
      handler(newVal) {
        this.debounceFieldChange('biologicalSex')(newVal);
      },
    },
    'model.email': {
      handler(newVal) {
        this.debounceFieldChange('email')(newVal);
      },
    },
    'model.streetNumber': {
      handler(newVal) {
        this.debounceFieldChange('streetNumber')(newVal);
      },
    },
    'model.phone': {
      handler(newVal) {
        this.debounceFieldChange('phone')(newVal);
      },
    },
    'model.addressComplement': {
      handler(newVal) {
        this.debounceFieldChange('addressComplement')(newVal);
      },
    },
  },

  methods: {
    ...mapActions([
      'createMdmPatient',
      'updateMdmPatient',
      'createAdmission',
      'authorizeAdmissionHealthInsuranceTransaction',
      'getPaymentByAdmissionId',
      'getMedicalOrdersByAdmissionId',
      'getMdmPatientById',
      'updateAdmissionQuestions',
      'getDataProviderExamsCategories',
      'checkAndSetT2Flow',
      'setAdmissionNotElegible',
    ]),
    ...mapMutations({
      registrationData: 'SET_REGISTRATION_DATA',
      setCpf: 'SET_CPF',
      resetRegistrationData: 'SET_CLEAR_REGISTRATIONDATA',
      setStartFlowPaymentChoice: 'global/SET_START_FLOW_PAYMENT_CHOICE',
      setQuestionsToSend: 'SET_TOSEND',
      setListCategoryExams: 'global/SET_LIST_CATEGORY_EXAMS',
    }),

    async submitMdm() {
      const mdmPatient = this.payloadBuilderMDM();
      const EXAM_IMAGE_CATEGORY = 3;

      let nextStep = 'questions_step';

      if (!this.mdmData.id) {
        return this.goNextStep(
          'check_service_desk',
          datalayerErrorMessage('submitMdm', PATIENT_WITHOUT_MDMID),
        );
        // await this.createMdmPatient(mdmPatient);
      } else {
        await this.updateMdmPatient(mdmPatient);
      }

      if (this.isCollaboratorFlow || this.admissionFlows.isInCompanyFlow) {
        return await this.createAdmissionVaccine();
      } else if (this.isT2FlowAllowed || this.isVaccineFlow) {
        await this.updateAdmission();
      } else {
        const unitIdStr = localStorage.getItem(LOCAL_STORAGE_UNIT_ID);
        const unitId = parseInt(unitIdStr);
        const params = {
          unitId,
          mdmId: this.mdmData.id,
        };
        let admission = this.currentAdmission;
        if (!admission?.id) {
          admission = await this.createAdmission(params);
        }
        addDynatraceIdentifier(this.cpfHidden, this.mdmData?.id, this.currentAdmission?.id);

        if (!admission) {
          return this.goNextStep(
            'check_service_desk',
            datalayerErrorMessage('submitMdm', PATIENT_WITHOUT_ADMISSION),
          );
        }
      }

      if (this.currentAdmission.source.origin === 'CEV') {
        return this.goNextStep('checkin_conclusion_simple');
      }

      if (this.hasExams.status) {
        const examsCodes = this.medicalOrders[0].exams.map((exam) => exam.examCode);
        const { content = [] } = await this.getDataProviderExamsCategories({ exams: examsCodes });
        this.setExamCategory(content);

        if (this.isAdmissionAuthorized) {
          const specialtiesExams = content.filter((ct) => ct.categoria.id === EXAM_IMAGE_CATEGORY);

          if (specialtiesExams.length === examsCodes.length) {
            nextStep = 'add_exams_check';
          }

          return this.goNextStep(nextStep);
        }
      }

      if (this.isVaccineFlow || this.isCollaboratorFlow) {
        return this.goNextStep('payment_pre_loading');
      }

      if (this.hasExams.status) {
        nextStep = this.currentAdmission.urlMedicalOrders ? 'exams_step' : 'upload_medical_order';
        return this.goNextStep(nextStep);
      }

      if (this.enumUnitSelected?.uploadExams) {
        this.setStartFlowPaymentChoice(true);
        return this.goNextStep('payment_type_choice');
      }

      return this.goNextStep(nextStep);
    },

    // Builds MDM Api payload
    payloadBuilderMDM() {
      let MDMPayload = {};
      const {
        cep,
        street,
        streetNumber,
        addressComplement,
        state,
        city,
        neighborhood,
        phone,
        email,
        name,
        biologicalSex,
        birthDate,
      } = this.model;

      const contacts = [];
      const normalizedEmail = removeAccents(email).toLowerCase();

      let phoneContactData = {
        contactType: 'CELULAR',
        value: this.unMasker(phone),
      };

      if (this.contactSelected) {
        phoneContactData = {
          ...phoneContactData,
          principal: true,
        };
      }

      contacts.push(phoneContactData, {
        contactType: 'EMAIL',
        value: normalizedEmail,
        principal: true,
      });

      const createPatientData = {
        name: name.trim(),
        biologicalSex,
        gender: biologicalSex,
        birthDate: this.normalizeDate(birthDate),
        documents: [
          {
            type: 'CPF',
            value: this.cpfSearched,
          },
        ],
      };

      const patientData = {
        contacts,
        addresses: [
          {
            state,
            city,
            street,
            number: streetNumber,
            complement: addressComplement,
            neighborhood,
            zipCode: this.unMasker(cep),
            principal: true,
            country: 'Brasil',
          },
        ],
      };

      if (this.formFunction === 'updateInfo') {
        const documents = this.mdmData.documents.filter((doc) => doc.type !== 'RG');
        MDMPayload = {
          ...this.mdmData,
          ...patientData,
          documents,
        };
      } else {
        // InCompany não tem cadastro de endereço
        // se enviarmos pro MDM um objeto dentro de addresses com zipCode vazio, não cria paciente
        if (this.admissionFlows.isInCompanyFlow) {
          delete patientData.addresses;
        }
        MDMPayload = {
          ...patientData,
          ...createPatientData,
        };
      }

      return MDMPayload;
    },
    setExamCategory(content) {
      const filterCategory = content.reduce((acc, cur) => {
        acc.push(cur.categoria.nome);
        const listCategories = acc.filter((category, i) => acc.indexOf(category) === i);
        return listCategories;
      }, []);

      const listCategories = filterCategory.reduce((acc, cur) => {
        if (cur === 'Análise Clínica') {
          acc.push('AC');
        } else if (cur === 'Diagnóstico por Imagem') {
          acc.push('RDI');
        } else if (this.isVaccineFlow) {
          acc.push('VACINA');
        } else {
          acc.push('OUTROS');
        }
        return acc;
      }, []);

      const categories = listCategories.join(' e ');
      this.setListCategoryExams(categories);
    },

    getContactsData(mdmContactData) {
      const neededContactTypesList = ['celular', 'email'];
      const contactsData = {
        celular: null,
        email: null,
      };

      neededContactTypesList.forEach((neededContactType) => {
        const normalizedContactType = neededContactType.toUpperCase();

        contactsData[neededContactType] = mdmContactData.find(({ contactType, principal }) => {
          return contactType === normalizedContactType && !!principal;
        });

        if (!contactsData[neededContactType]) {
          contactsData[neededContactType] = mdmContactData.find(({ contactType }) => {
            return contactType === normalizedContactType;
          });
        }

        if (this.contactSelected && this.chosenContactType === 'sms') {
          contactsData.celular = mdmContactData.find(({ id }) => {
            return this.contactSelected.id === id;
          });
        }
      });

      return contactsData;
    },

    getAddressData(mdmAddressesData) {
      let addressData = {};

      if (mdmAddressesData?.length) {
        addressData = mdmAddressesData.find(({ principal }) => !!principal);

        if (!addressData) {
          addressData = mdmAddressesData.find(({ addressType }) => addressType === 'RESIDENCIAL');
        }

        if (!addressData) {
          addressData = mdmAddressesData[0];
        }
      }

      return addressData;
    },

    // Sets patient data form
    setPatientExistingData() {
      const { name, birthDate, addresses = [], biologicalSex } = this.mdmData;

      const address = this.getAddressData(addresses);
      const contacts = this.getContactsData(this.mdmData.contacts);

      this.isFieldReadOnly();

      this.model = {
        name: name || '',
        birthDate: birthDate ? this.$moment(birthDate).format('DD/MM/YYYY') : '',
        email: contacts.email?.value || '',
        phone: contacts.celular?.value || '',
        biologicalSex: biologicalSex || '',
        cep: address?.zipCode || '',
        state: address?.state || '',
        city: address?.city || '',
        neighborhood: address?.neighborhood || '',
        street: address?.street || '',
        streetNumber: address?.number || '',
        addressComplement: address?.complement || '',
      };

      const modelEntries = Object.entries(this.model);
      modelEntries.forEach((entry) => {
        const key = entry[0];
        const value = entry[1];

        if (value === 'NAO_INFORMADO') {
          this.model[key] = '';
        }
      });

      const hasCepWithoutStateCity = this.model.cep && (!this.model.state || !this.model.city);

      if (hasCepWithoutStateCity) {
        this.resolveStateCity();
      }

      const patientLocalStorage = this.patientData;
      const cpfUserLogged = this.cpfSearched;

      if (patientLocalStorage?.cpf === cpfUserLogged) {
        this.model = patientLocalStorage?.data;
      } else {
        const model = this.model;
        this.registrationData(model);
        this.setCpf(cpfUserLogged);
      }
    },

    // Checks if field is supposed to be readOnly
    isFieldReadOnly() {
      const readOnlyFields = ['name', 'birthDate', 'biologicalSex'];
      this.schema.fields.map((field) => {
        readOnlyFields.forEach((fieldData) => {
          if (field.id.includes(fieldData) && this.mdmData[fieldData]) {
            field.readonly = true;
            if (fieldData === 'biologicalSex') {
              field.type = 'input';
            }
          }
        });
      });

      return true;
    },

    questions() {
      return [
        {
          answer: 'N/A',
          question: 'last_meal_type',
        },
        {
          answer: 'N/A',
          question: 'last_meal_instant',
        },
        {
          answer: 'no',
          question: 'taking_medications',
        },
        {
          answer: 'N/A',
          question: 'medications',
        },
      ];
    },

    /**
     * Returns address data and update "cepIsValid" flag
     * If CEP is valid, return CEP API data, if isn't, return empty address data
     * @Param {string} cep - the address zipCode
     */
    async searchAddressByCep(cep) {
      const fields = {
        street: '',
        neighborhood: '',
        city: '',
        state: '',
      };

      try {
        const data = await this.fetchCep(cep);
        this.cepIsValid = true;

        Object.assign(fields, {
          ...data,
          neighborhood: removeAccents(data.neighborhood),
          state: this.getBrazilStateFullname(data.state),
        });
      } catch {
        this.cepIsValid = false;
      }

      return fields;
    },

    /**
     * Updates address form inputs
     * @Param {object} addressData - Object with all updated address data
     */
    updateAddressFields(addressData) {
      addressData.addressComplement = '';
      addressData.streetNumber = '';
      Object.assign(this.model, addressData);
    },

    // Sets CEP error message
    cepErrorMessager() {
      const form = this.$refs.formTemplate.$refs.form;
      const inputId = 'registry-input-address-cep';
      const errorMessage = 'CEP inválido';

      form.setErrors({
        [inputId]: [errorMessage],
      });
    },

    normalizeDate(date) {
      const dateNormalized = date.split('/').reverse().join('-');
      return dateNormalized;
    },

    unMasker(value) {
      return value.replace(/[^0-9 ]/g, '');
    },
    async resolveStateCity() {
      const { state, city } = await this.fetchCep(this.model.cep);

      Object.assign(this.model, {
        state: this.getBrazilStateFullname(state),
        city,
      });
    },

    mountVaccineAdmissionPayload() {
      const { brandId, unitId } = this.getBrandUnitIdVariables;
      // Esse payload básico de vacina é usado para check-in InCompany
      const vaccinePayload = {
        unitId,
        brandId,
        mdmId: this.mdmData.id,
        medicalOrders: [
          {
            doctorIdentifierKind: '/',
            exams: [
              {
                examCode: '13385',
                status: 'WAITING',
                materialComplement: null,
                schedulingDate: this.$moment().format('YYYY-MM-DD[T]HH:mm:ss-03:00'),
                unitRealizationId: unitId,
              },
            ],
          },
        ],
      };

      if (this.isCollaboratorFlow) {
        const payment = {
          transactions: [
            {
              status: 'PENDING',
              transactionType: {
                type: 'HEALTH_INSURANCE',
              },
              clientId: '5775',
            },
          ],
        };

        const questions = this.questions();

        vaccinePayload.payment = payment;
        vaccinePayload.questions = questions;
      }

      return vaccinePayload;
    },

    /**
     * @method createAdmissionVaccine
     * @description Creates admission for vaccine campaigns
     */
    async createAdmissionVaccine() {
      const vaccinePayload = this.mountVaccineAdmissionPayload();

      const admission = await this.createAdmission(vaccinePayload);
      if (admission && this.isCollaboratorFlow) {
        const payment = await this.getPaymentByAdmissionId(admission.admissionId);
        const medicalOrders = await this.getMedicalOrdersByAdmissionId(admission.admissionId);
        const transaction = payment?.transactions[0];
        const exam = medicalOrders
          ? medicalOrders[0].exams
            ? medicalOrders[0].exams[0]
            : null
          : null;

        if (transaction && exam) {
          const authorizeParams = [
            {
              ...transaction,
              status: 'AUTHORIZED',
              authorizationDate: this.$moment().format('YYYY-MM-DD[T]HH:mm:ss-03:00'),
              examUuidList: [exam.uuid],
            },
          ];
          const authorized = await this.authorizeAdmissionHealthInsuranceTransaction({
            transactionId: transaction.uuid,
            payload: authorizeParams,
          });
          if (authorized) {
            return this.goNextStep('questions_step');
          }
        }
      } else if (admission && this.admissionFlows.isInCompanyFlow) {
        const insuranceOptionsRoute = {
          name: 'change-insurance',
          params: {
            path: 'cliente',
            header: {
              hidePreviousBtn: false,
              title: 'Escolha seu convênio',
            },
          },
        };

        return this.$router.push(insuranceOptionsRoute);
      } else {
        return this.goNextStep(
          'check_service_desk',
          datalayerErrorMessage('createAdmissionVaccine', ERROR_CREATE_ADMISSION_VACCINE),
        );
      }
    },

    async updateAdmission() {
      if (this.currentAdmission.id) {
        const updatedQuestions = this.createUpdatedQuestions(this.questions());

        for await (const question of updatedQuestions) {
          this.setQuestionsToSend({
            uuid: question.uuid,
            answer: question.answer,
            question: question.question,
          });
        }

        await this.updateAdmissionQuestions();
      }
    },
    createUpdatedQuestions(questions = []) {
      return questions.map((question) => {
        const questionTarget = this.currentAdmission.questions?.find(
          (el) => el.question === question.question,
        );

        question.uuid = questionTarget ? questionTarget.uuid : '';

        return question;
      });
    },
    debounceFieldChange(fieldName) {
      return debounce((newVal) => {
        this.registrationData({ ...this.model, [fieldName]: newVal });
      }, 3000);
    },
    validateAdmissionDate() {
      if (this.admission?.id && this.isT2FlowAllowed) {
        const dateAdmission = new Date(this.admission?.admissionDate).toLocaleDateString();
        const date = new Date().toLocaleDateString();
        const validateDate = dateAdmission === date;

        if (!validateDate) {
          this.setAdmissionNotElegible();
        }
      }
    },
  },

  async created() {
    const patientLocalStorage = this.patientData;
    const cpfUserLogged = this.cpfSearched;

    if (patientLocalStorage?.cpf !== cpfUserLogged) {
      this.resetRegistrationData();
    }
    if (this.mdmData?.id) {
      this.mostCommonPagesAnalytics({
        eventType: 'funnel_step',
        currentStep: 'Cadastro',
      });

      await this.getMdmPatientById(this.mdmData.id);
      this.setPatientExistingData();
      addDynatraceIdentifier(this.cpfHidden, this.mdmData?.id, this.currentAdmission?.id);
    } else {
      this.setCpf(cpfUserLogged);
      this.model = patientLocalStorage?.data;
      this.mdmCep = false;
    }

    await this.checkAndSetT2Flow();
    await this.validateAdmissionDate();
  },
};
</script>

<style lang="scss">
#patient-registry {
  .icon-circle {
    $size: 70px;

    height: $size;
    min-height: $size;
    min-width: $size;
    width: $size;

    > .di {
      &:before {
        font-size: 35px;
      }
    }
  }
}

.registry {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  margin-top: 78px;

  &__header {
    color: var(--color-grey-7);
    font-size: var(--font-size-2);
    line-height: 22px;
    margin: 0 0 16px 0;
    text-align: left;
  }

  &__form {
    width: 100%;
  }
}

.patient-registry {
  header {
    background-color: #ffffff;
    position: fixed;
    z-index: 1;
  }
}
</style>
